/* eslint-disable react-hooks/set-state-in-effect */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  TrendingUp, Package, PoundSterling, Receipt,
  Download, LogOut, RefreshCw, Search, ShoppingBag,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../components/ui/select';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const todayISO = () => new Date().toISOString().slice(0, 10);
const isoDaysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const FinanceDashboard = () => {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();

  const [start, setStart] = useState(isoDaysAgo(29));
  const [end, setEnd] = useState(todayISO());
  const [paymentMethod, setPaymentMethod] = useState('all');
  const [orderType, setOrderType] = useState('all');

  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Item-wise sales section
  const [itemSales, setItemSales] = useState({ rows: [], totals: null });
  const [itemSearch, setItemSearch] = useState('');
  const [itemSort, setItemSort] = useState('quantity_sold'); // or 'gross_revenue' | 'name'

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const fetchAll = useCallback(async () => {
    try {
      const params = { start, end };
      const txParams = { ...params, payment_method: paymentMethod, order_type: orderType, limit: 200 };
      const itemParams = { ...params, payment_method: paymentMethod, order_type: orderType };
      const [sumRes, txRes, itemRes] = await Promise.all([
        axios.get(`${API_URL}/api/finance/summary`, { ...auth, params }),
        axios.get(`${API_URL}/api/finance/transactions`, { ...auth, params: txParams }),
        axios.get(`${API_URL}/api/finance/item-sales`, { ...auth, params: itemParams }),
      ]);
      setSummary(sumRes.data);
      setTransactions(txRes.data.transactions || []);
      setTotalCount(txRes.data.total || 0);
      setItemSales({ rows: itemRes.data?.rows || [], totals: itemRes.data?.totals || null });
    } catch (err) {
      console.error('Failed to load finance data:', err);
      toast.error('Failed to load finance data');
    } finally {
      setLoading(false);
    }
  }, [start, end, paymentMethod, orderType, token]);

  useEffect(() => {
    let cancelled = false;
    // Don't toggle loading on every filter change — only show spinner on
    // the very first fetch. This also keeps us out of the React 19
    // `set-state-in-effect` rule's path.
    fetchAll().catch(() => {});
    return () => { cancelled = true; void cancelled; };
  }, [fetchAll]);

  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams({
        start, end,
        ...(paymentMethod !== 'all' && { payment_method: paymentMethod }),
        ...(orderType !== 'all' && { order_type: orderType }),
      }).toString();
      const res = await axios.get(`${API_URL}/api/finance/transactions.csv?${params}`, {
        ...auth, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `crispy-chicken-transactions-${todayISO()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV download started');
    } catch (err) {
      console.error('CSV export failed:', err);
      toast.error('CSV export failed');
    }
  };

  const handleExportItemsCSV = async () => {
    try {
      const params = new URLSearchParams({
        start, end,
        ...(paymentMethod !== 'all' && { payment_method: paymentMethod }),
        ...(orderType !== 'all' && { order_type: orderType }),
        ...(itemSearch && { search: itemSearch }),
      }).toString();
      const res = await axios.get(`${API_URL}/api/finance/item-sales.csv?${params}`, {
        ...auth, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `crispy-chicken-item-sales-${todayISO()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Item sales CSV download started');
    } catch (err) {
      console.error('Item CSV export failed:', err);
      toast.error('Item CSV export failed');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/finance/login');
  };

  const setPreset = (days) => {
    setStart(isoDaysAgo(days - 1));
    setEnd(todayISO());
  };

  const fmtGBP = (n) => `£${Number(n || 0).toFixed(2)}`;
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit',
  }) : '';

  const paymentMethodRow = (method) =>
    (summary?.by_payment_method || []).find(r => r.method === method) || { revenue: 0, count: 0 };
  const orderTypeRow = (type) =>
    (summary?.by_order_type || []).find(r => r.type === type) || { revenue: 0, count: 0 };

  // Client-side search + sort for the item-sales table. Kept in-memory so
  // the search box feels instant — the server-side `search` param is used
  // only when exporting the CSV so the file matches what's on screen.
  const filteredItemRows = React.useMemo(() => {
    const q = itemSearch.trim().toLowerCase();
    let rows = q
      ? (itemSales.rows || []).filter((r) => (r.name || '').toLowerCase().includes(q))
      : (itemSales.rows || []);
    rows = [...rows].sort((a, b) => {
      if (itemSort === 'name') return (a.name || '').localeCompare(b.name || '');
      return (b[itemSort] || 0) - (a[itemSort] || 0);
    });
    return rows;
  }, [itemSales.rows, itemSearch, itemSort]);

  const filteredItemTotals = React.useMemo(() => ({
    total_items_sold: filteredItemRows.reduce((n, r) => n + (r.quantity_sold || 0), 0),
    total_gross_revenue: filteredItemRows.reduce((n, r) => n + (r.gross_revenue || 0), 0),
    unique_items: filteredItemRows.length,
  }), [filteredItemRows]);

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <header className="bg-white border-b" data-testid="finance-header">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-emerald-600 flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="font-bold leading-tight">Finance Console</p>
              <p className="text-xs text-muted-foreground leading-tight">
                {user?.email}
              </p>
            </div>
          </div>
          <Button variant="ghost" onClick={handleLogout} data-testid="finance-logout-button">
            <LogOut className="h-4 w-4 mr-2" /> Logout
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 space-y-8">
        {/* Filters */}
        <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid="finance-filters">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
            <div>
              <Label htmlFor="fin-start">From</Label>
              <Input id="fin-start" type="date" value={start}
                onChange={(e) => setStart(e.target.value)}
                className="mt-1.5" data-testid="finance-start-date" />
            </div>
            <div>
              <Label htmlFor="fin-end">To</Label>
              <Input id="fin-end" type="date" value={end}
                onChange={(e) => setEnd(e.target.value)}
                className="mt-1.5" data-testid="finance-end-date" />
            </div>
            <div>
              <Label>Payment</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger className="mt-1.5" data-testid="finance-payment-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="cod">Cash on Delivery</SelectItem>
                  <SelectItem value="coc">Cash on Collection</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Order Type</Label>
              <Select value={orderType} onValueChange={setOrderType}>
                <SelectTrigger className="mt-1.5" data-testid="finance-type-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="delivery">Delivery</SelectItem>
                  <SelectItem value="collection">Collection</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 md:col-span-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setPreset(1)}>Today</Button>
              <Button variant="outline" size="sm" onClick={() => setPreset(7)}>7d</Button>
              <Button variant="outline" size="sm" onClick={() => setPreset(30)}>30d</Button>
              <Button variant="outline" size="sm" onClick={fetchAll}
                data-testid="finance-refresh-button">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* KPI cards */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          <KPI icon={<Package className="h-7 w-7 text-blue-500" />}
            label="Total Orders" value={summary?.total_orders ?? 0}
            testid="finance-kpi-orders" loading={loading} />
          <KPI icon={<PoundSterling className="h-7 w-7 text-emerald-600" />}
            label="Total Revenue" value={fmtGBP(summary?.total_revenue)}
            testid="finance-kpi-revenue" loading={loading} />
          <KPI icon={<TrendingUp className="h-7 w-7 text-primary" />}
            label="Avg Order Value" value={fmtGBP(summary?.avg_order_value)}
            testid="finance-kpi-aov" loading={loading} />
        </motion.div>

        {/* Breakdowns */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <BreakdownCard
            title="By Payment Method"
            rows={[
              { label: 'Card', ...paymentMethodRow('card') },
              { label: 'Cash on Delivery', ...paymentMethodRow('cod') },
              { label: 'Cash on Collection', ...paymentMethodRow('coc') },
            ]}
            fmtGBP={fmtGBP}
            testid="finance-breakdown-payment"
          />
          <BreakdownCard
            title="By Order Type"
            rows={[
              { label: 'Delivery', ...orderTypeRow('delivery') },
              { label: 'Collection', ...orderTypeRow('collection') },
            ]}
            fmtGBP={fmtGBP}
            testid="finance-breakdown-type"
          />
        </div>

        {/* Transactions */}
        <div className="bg-white rounded-2xl shadow-sm" data-testid="finance-transactions-card">
          <div className="p-5 border-b flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold">Transactions</h3>
              <p className="text-xs text-muted-foreground">
                Showing {transactions.length} of {totalCount}
              </p>
            </div>
            <Button onClick={handleExportCSV} className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="finance-export-csv">
              <Download className="h-4 w-4 mr-2" /> Export CSV
            </Button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40 text-left">
                <tr>
                  <th className="p-3 font-semibold">Order #</th>
                  <th className="p-3 font-semibold">Date</th>
                  <th className="p-3 font-semibold">Customer</th>
                  <th className="p-3 font-semibold">Type</th>
                  <th className="p-3 font-semibold">Payment</th>
                  <th className="p-3 font-semibold">Status</th>
                  <th className="p-3 font-semibold text-right">Subtotal</th>
                  <th className="p-3 font-semibold text-right">Delivery</th>
                  <th className="p-3 font-semibold text-right">Tip</th>
                  <th className="p-3 font-semibold text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={10} className="p-8 text-center text-muted-foreground">Loading…</td></tr>
                ) : transactions.length === 0 ? (
                  <tr><td colSpan={10} className="p-8 text-center text-muted-foreground">
                    <Receipt className="h-10 w-10 mx-auto mb-2 opacity-30" />
                    No transactions for the selected filters.
                  </td></tr>
                ) : transactions.map(tx => (
                  <tr key={tx.id} className="border-t hover:bg-secondary/30"
                    data-testid={`finance-tx-row-${tx.id}`}>
                    <td className="p-3 font-mono">
                      {tx.daily_order_number ? `#${String(tx.daily_order_number).padStart(3, '0')}` : (tx.id || '').slice(0, 8)}
                    </td>
                    <td className="p-3 whitespace-nowrap">{fmtDate(tx.created_at)}</td>
                    <td className="p-3">{tx.customer_name}</td>
                    <td className="p-3 capitalize">{tx.order_type}</td>
                    <td className="p-3 capitalize">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        tx.payment_method === 'cod'
                          ? 'bg-amber-100 text-amber-800'
                          : tx.payment_method === 'coc'
                            ? 'bg-orange-100 text-orange-800'
                            : 'bg-blue-100 text-blue-800'
                      }`}>
                        {tx.payment_method === 'cod'
                          ? 'COD'
                          : tx.payment_method === 'coc'
                            ? 'COC'
                            : 'Card'}
                      </span>
                    </td>
                    <td className="p-3 capitalize">{(tx.payment_status || '').toLowerCase()}</td>
                    <td className="p-3 text-right">{fmtGBP(tx.subtotal)}</td>
                    <td className="p-3 text-right">{fmtGBP(tx.delivery_fee)}</td>
                    <td className="p-3 text-right">{Number(tx.tip) > 0 ? fmtGBP(tx.tip) : '—'}</td>
                    <td className="p-3 text-right font-bold">{fmtGBP(tx.total_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Item-wise sales */}
        <div className="bg-white rounded-2xl shadow-sm" data-testid="finance-item-sales-card">
          <div className="p-5 border-b flex flex-col md:flex-row md:items-end gap-3 md:gap-4">
            <div className="flex-1">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <ShoppingBag className="h-5 w-5 text-emerald-600" /> Item sales
              </h3>
              <p className="text-xs text-muted-foreground">
                Aggregated per menu item across the selected date range and filters.
                Refunded orders still count towards items sold.
              </p>
            </div>
            <div className="flex-1 md:max-w-xs">
              <Label htmlFor="item-search" className="text-xs">Search item</Label>
              <div className="relative mt-1.5">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="item-search"
                  data-testid="finance-item-search"
                  className="pl-9"
                  placeholder="e.g. burger, wrap, churros"
                  value={itemSearch}
                  onChange={(e) => setItemSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="md:w-40">
              <Label className="text-xs">Sort by</Label>
              <Select value={itemSort} onValueChange={setItemSort}>
                <SelectTrigger className="mt-1.5" data-testid="finance-item-sort">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="quantity_sold">Qty sold ↓</SelectItem>
                  <SelectItem value="gross_revenue">Revenue ↓</SelectItem>
                  <SelectItem value="name">Name (A–Z)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={handleExportItemsCSV}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="finance-item-export-csv"
            >
              <Download className="h-4 w-4 mr-2" /> Export CSV
            </Button>
          </div>

          {/* Totals strip */}
          <div className="px-5 py-3 grid grid-cols-3 gap-4 text-sm border-b bg-secondary/40" data-testid="finance-item-totals">
            <div>
              <p className="text-muted-foreground text-xs">Unique items</p>
              <p className="font-bold">{filteredItemTotals.unique_items}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">Total qty sold</p>
              <p className="font-bold">{filteredItemTotals.total_items_sold}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">Gross revenue</p>
              <p className="font-bold">{fmtGBP(filteredItemTotals.total_gross_revenue)}</p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left bg-secondary/60">
                  <th className="p-3">Item</th>
                  <th className="p-3">Type</th>
                  <th className="p-3 text-right">Qty sold</th>
                  <th className="p-3 text-right">Orders</th>
                  <th className="p-3 text-right">Gross revenue</th>
                  <th className="p-3">Last sold</th>
                </tr>
              </thead>
              <tbody>
                {filteredItemRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-muted-foreground">
                      No item sales in this range.
                    </td>
                  </tr>
                ) : filteredItemRows.map((r) => (
                  <tr key={r.item_id || r.name} className="border-t hover:bg-secondary/30" data-testid="finance-item-row">
                    <td className="p-3 font-medium">{r.name || '(deleted item)'}</td>
                    <td className="p-3">
                      <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full ${
                        r.item_type === 'bundle' ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {r.item_type === 'bundle' ? 'Bundle' : 'Standard'}
                      </span>
                    </td>
                    <td className="p-3 text-right font-bold">{r.quantity_sold}</td>
                    <td className="p-3 text-right">{r.orders_count}</td>
                    <td className="p-3 text-right">{fmtGBP(r.gross_revenue)}</td>
                    <td className="p-3 text-xs text-muted-foreground">{fmtDate(r.last_sold_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const KPI = ({ icon, label, value, testid, loading }) => (
  <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">{icon}</div>
    <p className="text-3xl font-bold">{loading ? '—' : value}</p>
    <p className="text-sm text-muted-foreground">{label}</p>
  </div>
);

const BreakdownCard = ({ title, rows, fmtGBP, testid }) => (
  <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid={testid}>
    <h3 className="text-lg font-bold mb-4">{title}</h3>
    <div className="space-y-3">
      {rows.map(r => (
        <div key={r.label} className="flex items-center justify-between border-b last:border-b-0 pb-2 last:pb-0">
          <div>
            <p className="font-medium">{r.label}</p>
            <p className="text-xs text-muted-foreground">{r.count} order{r.count !== 1 ? 's' : ''}</p>
          </div>
          <p className="font-bold">{fmtGBP(r.revenue)}</p>
        </div>
      ))}
    </div>
  </div>
);

export default FinanceDashboard;
